/**
 * ENG-436 — open state for the global Luma drawer (`luma-floating.vue`).
 *
 * The drawer used to own its `isOpen` flag privately, which made it unreachable
 * from anywhere else: a screen that wanted to hand Luma a question had no way in
 * and had to navigate to the full-screen `/chats/:sessionId` view instead. Held
 * here, the flag lets any screen open the drawer in place — and hand over the
 * question it should ask on the way.
 */

import type { ChatSurface } from '@/composables/analytics/analytics-service';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useLumaDockStore = defineStore('luma-dock', () => {
   const isOpen = ref(false);

   /**
    * A question handed over for Luma to ask.
    *
    * Parked here rather than sent, because the store doesn't own the
    * conversation. The drawer does: it drains this (see `takePendingAsk`) and
    * asks it in a thread of its own.
    */
   const pendingQuestion = ref<string | null>(null);

   /**
    * Which input the pending question came from.
    *
    * Travels with the question because the drawer opens over every screen, and
    * once the question arrives there is nothing left to say whether the user
    * typed it in the drawer or in the search bar that routed it here.
    */
   const pendingSurface = ref<ChatSurface | null>(null);

   function open() {
      isOpen.value = true;
   }

   function clearPending() {
      pendingQuestion.value = null;
      pendingSurface.value = null;
   }

   function close() {
      isOpen.value = false;
      // A question nobody got to ask must not resurface on the next open.
      clearPending();
   }

   /**
    * Open the drawer on `question`, asked from `surface`.
    *
    * Safe to call while the drawer is already open: the question is asked in a
    * fresh thread either way, so it never lands in the middle of an unrelated
    * conversation.
    */
   function askLuma(question: string, surface: ChatSurface) {
      const trimmed = question.trim();
      if (!trimmed)
         return;

      pendingQuestion.value = trimmed;
      pendingSurface.value = surface;
      isOpen.value = true;
   }

   /** Read the handed-over ask and clear it, so it is asked exactly once. */
   function takePendingAsk(): { question: string; surface: ChatSurface } | null {
      const question = pendingQuestion.value;
      if (!question)
         return null;

      const surface = pendingSurface.value ?? 'chat_drawer';
      clearPending();
      return { question, surface };
   }

   const $reset = () => {
      isOpen.value = false;
      clearPending();
   };

   return {
      isOpen,
      pendingQuestion,
      pendingSurface,

      open,
      close,
      askLuma,
      takePendingAsk,

      $reset,
   };
});
